import { useState } from "react";
import Papa from "papaparse";
import { adminApi } from "../../services/adminApi";
import { complianceApi } from "../../services/complianceApi";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { RevertPanel } from "../../components/security/RevertPanel";

/**
 * GET /management/audit-log (api/functions/admin.py) is real and already
 * searches/filters by user_id, table, and action over audit.AuditLog --
 * every INSERT/UPDATE/DELETE across app./audit. schemas lands here
 * automatically via V020's generated triggers, PII-redacted by
 * audit.fn_SanitizeDetails before it's ever written (see that migration's
 * header comment for exactly which keys get masked). That redaction is
 * real and already applied server-side, not a frontend filter.
 *
 * "Admin Action Audit" isn't a separate table -- admin mutations that touch
 * a table (feature-flag toggles, support-ticket status changes, maintenance
 * mode via dbo.AppConfig) already land in this same audit.AuditLog through
 * the generic triggers, so filtering this page's `action`/`table` fields to
 * admin-relevant values *is* the admin action audit, not a missing feature.
 * Read-only admin analytics queries (viewing a chart) naturally produce no
 * rows here since they don't mutate anything -- that's expected, not a gap.
 */
export function AuditLogPage() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ user_id: "", table: "", action: "" });
  const [loading, setLoading] = useState(false);
  // The entry being considered for a revert, or null (#315). One at a
  // time, deliberately: "selective, row by row, reviewed" is the whole
  // design, and a screen that could revert several at once would be the
  // bulk button the task exists to argue against.
  const [reverting, setReverting] = useState(null);

  async function search() {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await adminApi.searchAuditLog(params);
      setRows(r.audit_log);
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!rows?.length) return;
    const csv = Papa.unparse(rows.map((r) => ({ ...r, details: JSON.stringify(r.details) })));
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function exportPdf() {
    try {
      const response = await complianceApi.auditLogPdfExport(filters);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "audit-log.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.apiMessage || "Failed to export PDF. Use CSV export as a fallback.");
    }
  }

  return (
    <div>
      <h1>Security Audit Log</h1>
      <p style={{ color: "var(--color-text-muted)" }}>
        7-year retention per compliance policy (audit.AuditLog has no automated purge job configured -- retention is
        currently "forever by default," which satisfies a 7-year minimum but should get an explicit purge policy once
        one is decided). All PII is masked server-side before rows are written here.
      </p>

      <Card title="Search" className="section-spacing">
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <label>User ID
            <input value={filters.user_id} onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))} className="touch-target" style={{ display: "block" }} />
          </label>
          <label>Table
            <input value={filters.table} onChange={(e) => setFilters((f) => ({ ...f, table: e.target.value }))} className="touch-target" style={{ display: "block" }} placeholder="app.Hives" />
          </label>
          <label>Action
            <select value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))} className="touch-target">
              <option value="">Any</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </label>
          <Button onClick={search} disabled={loading}>{loading ? "Searching…" : "Search"}</Button>
          <Button variant="secondary" onClick={exportCsv} disabled={!rows?.length}>Export CSV</Button>
          <Button variant="secondary" onClick={exportPdf} disabled={!rows?.length}>Export PDF</Button>
        </div>
      </Card>

      {reverting !== null && (
        <Card title={`Revert entry ${reverting}`} className="section-spacing"
              actions={<Button size="sm" variant="ghost" onClick={() => setReverting(null)}>Close</Button>}>
          <RevertPanel logId={reverting} onDone={search} />
        </Card>
      )}

      <Card>
        <DataTable
          rows={rows}
          loading={loading}
          rowKey="log_id"
          emptyTitle={rows === null ? "Run a search to see results" : "No matching audit entries"}
          columns={[
            { key: "timestamp", header: "When", render: (r) => new Date(r.timestamp).toLocaleString() },
            { key: "user_id", header: "User ID" },
            { key: "action", header: "Action" },
            { key: "table_affected", header: "Table" },
            { key: "record_id", header: "Record ID" },
            { key: "details", header: "Details (PII-redacted)", render: (r) => <code style={{ fontSize: "0.75rem" }}>{JSON.stringify(r.details).slice(0, 120)}</code> },
            {
              key: "revert",
              header: "",
              // Only on UPDATEs. An INSERT or DELETE needs a separate
              // decision about dependent rows, and the server refuses
              // them -- offering a button that always refuses would
              // teach operators to ignore this column.
              render: (r) => (r.action === "UPDATE" ? (
                <Button size="sm" variant="secondary"
                        onClick={() => setReverting(r.log_id)}>
                  Review revert
                </Button>
              ) : null),
            },
          ]}
        />
      </Card>
    </div>
  );
}
